import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'lib/api/apiClient';
import { SEARCH_URLS } from 'lib/api/endpoints';

export const searchCatalog = createAsyncThunk(
  'catalog/searchCatalog',
  async ({ coordinates, dateRange }, thunkAPI) => {
    try {
      const requestBody = {
        coordinates: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
        dates: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      };
      const response = await apiClient.post(SEARCH_URLS.catalog, requestBody, {
        signal: thunkAPI.signal,
      });
      const data = response.data;
      const payload = data?.data ?? data;
      return payload?.items || [];
    } catch (error) {
      if (error.response?.status === 504) {
        return thunkAPI.rejectWithValue('AOI too large. Try with a smaller AOI.');
      }
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Catalog search failed');
    }
  }
);

const catalogSlice = createSlice({
  name: 'catalog',
  initialState: {
    imageryData: [],
    catalogDateRange: null,
    taskingDateRange: null,
    selectedImages: [],
    visibleImage: null,
    hoveredImage: null,
    satelliteVisibility: {},
    searchLoading: false,
    searchError: null,
  },
  reducers: {
    setCatalogDateRange(state, action) {
      state.catalogDateRange = action.payload;
    },
    setTaskingDateRange(state, action) {
      state.taskingDateRange = action.payload;
    },
    setSelectedImages(state, action) {
      state.selectedImages = action.payload;
    },
    toggleImageSelection(state, action) {
      const image = action.payload;
      const idx = state.selectedImages.findIndex((i) => i.id === image.id);
      if (idx >= 0) {
        state.selectedImages.splice(idx, 1);
      } else {
        state.selectedImages.push(image);
      }
    },
    setVisibleImage(state, action) {
      state.visibleImage = action.payload;
    },
    setHoveredImage(state, action) {
      state.hoveredImage = action.payload;
    },
    setSatelliteVisibility(state, action) {
      state.satelliteVisibility = action.payload;
    },
    toggleSatelliteVisibility(state, action) {
      const satellite = action.payload;
      state.satelliteVisibility[satellite] = !state.satelliteVisibility[satellite];
    },
    setImageryData(state, action) {
      state.imageryData = action.payload;
    },
    setSearchLoading(state, action) {
      state.searchLoading = action.payload;
    },
    clearCatalogResults(state) {
      state.imageryData = [];
      state.selectedImages = [];
      state.visibleImage = null;
      state.hoveredImage = null;
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchCatalog.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchCatalog.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.imageryData = action.payload;
        // Clear stale selections from previous search
        state.selectedImages = [];
        state.visibleImage = null;
        state.hoveredImage = null;
        state.satelliteVisibility = {};
      })
      .addCase(searchCatalog.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.searchLoading = false;
        state.searchError = action.payload;
      });
  },
});

export const {
  setCatalogDateRange,
  setTaskingDateRange,
  setSelectedImages,
  toggleImageSelection,
  setVisibleImage,
  setHoveredImage,
  setSatelliteVisibility,
  toggleSatelliteVisibility,
  setImageryData,
  setSearchLoading,
  clearCatalogResults,
} = catalogSlice.actions;
export default catalogSlice.reducer;
